import type { CampaignImpression } from '@kimuno/api-sdk';

import {
	readSessionLock,
	readSnoozedCampaignIds,
	resolveCampaignSessionId,
	snoozeCampaign,
	writeSessionLock,
} from './campaignSession';
import type { CampaignConfig } from './types';

interface Candidate {
	config: CampaignConfig;
	impression: CampaignImpression | undefined;
	registeredAt: number;
}

export interface CampaignManager {
	request: (config: CampaignConfig, impression: CampaignImpression | undefined) => void;
	withdraw: (campaignId: string) => void;
	recordSeen: (campaignId: string) => void;
	snooze: (campaignId: string) => void;
	getGrantedId: () => string | null;
	subscribe: (listener: () => void) => () => void;
}

const isDisplayable = (candidate: Candidate, now: number): boolean => {
	const { config, impression } = candidate;
	if (impression?.dismissedAt) {
		return false;
	}
	if ((impression?.count ?? 0) >= config.maxDisplays) {
		return false;
	}
	if (config.cooldownMs > 0 && impression?.lastDisplayedAt) {
		const lastDisplayedAt = new Date(impression.lastDisplayedAt).getTime();
		if (!Number.isNaN(lastDisplayedAt) && now - lastDisplayedAt < config.cooldownMs) {
			return false;
		}
	}
	return true;
};

export const createCampaignManager = (): CampaignManager => {
	const candidates = new Map<string, Candidate>();
	const listeners = new Set<() => void>();
	let grantedId: string | null = null;
	let sessionId: string | null = null;
	let registrationCounter = 0;
	let evaluationScheduled = false;

	const notify = () => {
		for (const listener of listeners) {
			listener();
		}
	};

	const evaluate = () => {
		// Resolution can only fail before this point; a null sessionId means the
		// resolver hasn't finished — fail closed and wait for the next schedule.
		if (grantedId !== null || sessionId === null) {
			return;
		}
		if (readSessionLock(sessionId) !== null) {
			return;
		}
		const snoozedIds = readSnoozedCampaignIds(sessionId);
		const now = Date.now();
		let winner: Candidate | null = null;
		for (const candidate of candidates.values()) {
			if (snoozedIds.includes(candidate.config.id) || !isDisplayable(candidate, now)) {
				continue;
			}
			if (
				winner === null ||
				candidate.config.priority > winner.config.priority ||
				(candidate.config.priority === winner.config.priority &&
					candidate.registeredAt < winner.registeredAt)
			) {
				winner = candidate;
			}
		}
		if (winner) {
			grantedId = winner.config.id;
			notify();
		}
	};

	// Deferred one-tick grant: all campaigns that become eligible in the same
	// tick land in `candidates` before the first evaluation runs, so the highest
	// priority wins outright and the loser never renders (no flicker). Deferral
	// rides native promise microtasks (not queueMicrotask/setTimeout) so fake
	// timers in tests can't strand a pending evaluation.
	const scheduleEvaluation = () => {
		if (evaluationScheduled) {
			return;
		}
		evaluationScheduled = true;
		void (async () => {
			try {
				await Promise.resolve();
				if (sessionId === null) {
					sessionId = await resolveCampaignSessionId();
				}
			} finally {
				evaluationScheduled = false;
			}
			evaluate();
		})();
	};

	return {
		request: (config, impression) => {
			const existing = candidates.get(config.id);
			candidates.set(config.id, {
				config,
				impression,
				registeredAt: existing?.registeredAt ?? registrationCounter++,
			});
			scheduleEvaluation();
		},

		withdraw: (campaignId) => {
			if (!candidates.delete(campaignId)) {
				return;
			}
			if (grantedId === campaignId) {
				grantedId = null;
				notify();
				scheduleEvaluation();
			}
		},

		recordSeen: (campaignId) => {
			if (grantedId !== campaignId || sessionId === null) {
				return;
			}
			writeSessionLock(sessionId, campaignId);
		},

		snooze: (campaignId) => {
			if (sessionId !== null) {
				snoozeCampaign(sessionId, campaignId);
			}
			if (grantedId === campaignId) {
				grantedId = null;
				notify();
				scheduleEvaluation();
			}
		},

		getGrantedId: () => grantedId,

		subscribe: (listener) => {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
};

export const campaignManager = createCampaignManager();
